<template>
  <navbar-menu-tool v-model="computedValue" v-bind="toolBind" :target="$attrs.target">
    <template #content>
      <v-row no-gutters class="pa-3">
        <v-col cols="12">
          <template v-if="loading || done">
            <v-row
              no-gutters
              align="center"
              class="parcels-search-element parcels-search-element-success text-body-2 font-weight-medium success--text pa-2 mb-1"
            >
              <v-col cols="auto" class="mr-1">
                <v-icon size="18" color="success">mdi-check-circle</v-icon>
              </v-col>
              <v-col cols="auto" style="font-size: 12px !important">
                {{ `${$i18n.t('sidebar.uldk.searchedResult')}: ${parcels.length}/${inputTerrits.length}` }}
              </v-col>
            </v-row>
            <v-row
              no-gutters
              align="center"
              class="parcels-search-element parcels-search-element-error text-body-2 font-weight-medium error--text pa-2 mb-1 flex-nowrap"
              v-for="(error, idx) in errors"
              :key="idx"
            >
              <v-col cols="auto" class="mr-1">
                <v-icon size="18" color="error">mdi-close-circle</v-icon>
              </v-col>
              <v-col cols="auto" style="font-size: 12px !important" class="flex-shrink-1 text-truncate">
                {{ error.territ }}
              </v-col>
            </v-row>
          </template>
          <template v-else>
            <data-input v-model="searchValue" v-bind="searchBind" />
          </template>
        </v-col>
      </v-row>
      <main-input-dialog
        v-if="dataSourceDialogLoaded"
        :is-visible.sync="dataSourceDialogVisible"
        :loading="dataSourceDialogLoading"
        :title-translation="$i18n.t('dialog.title.saveResultToDataSource')"
        dialog-width="30%"
        agree-text="dialog.agreeSave"
        @agree="saveToDataSource"
        :inputs="dataSourceDialogInputs"
      >
        <template #[`dataSourceName.input`]="data">
          <v-col cols="6">
            <data-input v-model="dataSourceName" v-bind="data" />
          </v-col>
        </template>
        <template
          v-for="(name, idx) of ['voivodeship', 'county', 'commune', 'region', 'sheet', 'numer', 'teryt']"
          #[`${name}.input`]="data"
        >
          <v-col cols="6" :key="idx">
            <data-input v-model="dataSourceParams[name]" v-bind="data" />
          </v-col>
        </template>
        <template #[`skipDuplicates.input`]="data">
          <v-col cols="6">
            <data-input v-model="skipDuplicates" v-bind="data">
              <template #label>
                <span :class="{ 'font--text': !data.disabled }"> {{ $i18n.t(data.translationPath) }} </span>
              </template>
            </data-input>
          </v-col>
        </template>
      </main-input-dialog>
    </template>
    <template #[`content.footer`]>
      <template v-if="done">
        <dots-menu
          is-absolute
          font-size="19px"
          :disabled="!parcels.length || loading"
          :items="dotsItems"
          @toShpFile="downloadShapefile"
          @toDataSource="openDataSourceDialog"
        >
          <template #activator="{ on }">
            <base-button class="mx-3" :loading="loading" :disabled="!searchValue" v-on="on">
              {{ $i18n.t('button.saveResult') }}
            </base-button>
          </template>
        </dots-menu>
        <base-button type="alternative" @click="reset(true)" :disabled="loading">
          {{ $i18n.t('sidebar.uldk.reset') }}
        </base-button>
      </template>
      <template v-else>
        <base-button class="ml-3" icon="mdi-magnify" :loading="loading" :disabled="!searchValue" @click="search()">
          {{ $i18n.t('dialog.agreeSearch') }}
        </base-button>
      </template>
    </template>
  </navbar-menu-tool>
</template>
<script>
let LIMIT = 1000;

import { call, get } from 'vuex-pathify';

import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import NavbarMenuTool from '@/components/NavbarMenuTool';

import WKT from 'ol/format/WKT';
import { Style, Stroke, Fill, Text } from 'ol/style';
import { writeGeometryToGeoJSON } from '@/assets/js/mapUtils';

export default {
  name: 'ParcelsSearchMenuTool',
  components: { DataInput, DotsMenu, NavbarMenuTool, MainInputDialog: () => import('@/components/MainInputDialog') },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    computedValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    searchBind() {
      return {
        dataType: { name: 'textarea' },
        hideDetails: true,
        height: 'calc(59vh - 52px - 50px - 35px)',
        placeholder: this.$i18n.t('sidebar.uldk.multipleSearchHint'),
        rules: [v => !!v || 'rules.required'],
        disabled: this.loading,
      };
    },
    toolBind() {
      return {
        menuData: { name: 'parcelsSearch', position: 'left', allowOtherTop: true },
        buttonBind: { verboseName: this.$i18n.t('sidebar.uldk.multipleParcelsSearch') },
        hideActivator: true,
        hasCloseHeader: true,
        hasFooter: true,
      };
    },

    dataSources: get('layers/metadata'),
    polygonDataSourcesArray() {
      return this.$_objectToArray(this.dataSources).filter(
        dataSource => dataSource.geometry_type === 'polygon' || dataSource.geometry_type === 'multipolygon'
      );
    },
    dataSourceTextAttributes() {
      return (this.dataSources[this.dataSourceName]?.attributes_schema.attributes || [])
        .filter(attribute => attribute.data_type?.name === 'text')
        .map(attribute => {
          const { name: value, verbose_name: text } = attribute;
          return { text, value };
        });
    },
    dataSourceDialogInputs() {
      return [
        {
          name: 'dataSourceName',
          translationPath: 'dialog.dataSource',
          subtitle: 'sidebar.uldk.dataSourceSubtitle',
          dataType: {
            name: 'select',
          },
          items: this.polygonDataSourcesArray,
          itemValue: 'name',
          itemText: 'verbose_name',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        ...['voivodeship', 'county', 'commune', 'region', 'sheet', 'numer', 'teryt'].map(attribute => {
          return {
            name: attribute,
            translationPath: `sidebar.uldk.attributes.${attribute}`,
            cols: '12',
            dataType: { name: 'select' },
            items: this.dataSourceTextAttributes,
            clearable: true,
            disabled: !this.dataSourceName,
          };
        }),
        {
          name: 'skipDuplicates',
          translationPath: 'sidebar.uldk.skipDuplicates',
          persistentHint: true,
          hint: this.$i18n.t('sidebar.uldk.skipDuplicatesSubtitle'),
          cols: '12',
          leftCol: 0,
          dataType: {
            name: 'checkbox',
          },
          disabled: !this.dataSourceParams['teryt'],
        },
      ];
    },

    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
  },
  data: () => ({
    currentIntervalIndex: 0,
    done: false,
    dotsItems: [{ name: 'toShpFile' }, { name: 'toDataSource' }],
    errors: [],
    inputTerrits: [],
    interval: undefined,
    loading: false,
    parcels: [],
    searchValue: '',

    dataSourceDialogLoaded: false,
    dataSourceDialogVisible: false,
    dataSourceDialogLoading: false,
    dataSourceName: '',
    skipDuplicates: false,
    dataSourceParams: {},
  }),
  methods: {
    searchUldkParcel: call('search/searchUldkParcel'),
    reset(clear = false) {
      this.done = false;
      this.$root.$emit('toggleCustomVectorLayer', { id: 'parcelsSearch' });
      if (clear) this.searchValue = '';
      this.inputTerrits = [];
      this.parcels = [];
      this.errors = [];
    },
    readData(value = this.searchValue) {
      this.reset();
      this.$papa.parse(value, {
        complete: results => {
          this.inputTerrits = Array.from(new Set(results.data[0])).filter(territ => !!territ);
          if (this.inputTerrits?.length > LIMIT) {
            this.reset();
            this.$store.set('snackbar/PUSH_ERROR!', {
              message: this.$i18n.t('sidebar.uldk.parcelsAddingLimit', { limit: LIMIT }),
            });
          }
        },
        skipEmptyLines: true,
      });
    },
    search(value = this.searchValue) {
      this.loading = true;
      this.readData(value);
      if (!this.inputTerrits.length) {
        this.loading = false;
        return;
      }
      this.currentIntervalIndex = 0;
      this.errors = [];
      this.parcels = [];
      this.interval = setInterval(() => {
        this.searchParcel(this.inputTerrits[this.currentIntervalIndex]);
        this.currentIntervalIndex++;
        if (this.currentIntervalIndex >= this.inputTerrits.length) {
          clearInterval(this.interval);
          this.interval = undefined;
        }
      }, 1000);
    },
    async searchParcel(territ) {
      try {
        const parcel = await this.searchUldkParcel({
          territ,
          params: ['geom_wkt', 'numer', 'teryt', 'voivodeship', 'commune', 'county', 'region'],
          mapped: true,
        });
        let { geom_wkt: wkt } = parcel;
        if (wkt.indexOf(';') >= 0) wkt = wkt.split(';')[1];
        const geom = new WKT().readGeometry(wkt, {
          dataProjection: this.$_config.defaultEpsg,
          featureProjection: this.$_config.defaultEpsg,
        });
        parcel.geometry = writeGeometryToGeoJSON(geom);

        const territValue = parcel.teryt;
        const dotsIndexes = [];
        for (let i = 0; i < territValue.length; i++) if (territValue[i] === '.') dotsIndexes.push(i);
        if (dotsIndexes.length > 2) parcel.sheet = territValue.substring(dotsIndexes[1] + 1, dotsIndexes[2]);
        this.parcels.push(parcel);
      } catch {
        this.errors.push({ territ });
      } finally {
        if (!this.interval && this.parcels.length + this.errors.length >= this.inputTerrits.length) {
          this.done = true;
          this.loading = false;
          this.$root.$emit('toggleCustomVectorLayer', {
            id: 'parcelsSearch',
            geometry: this.getGeojson(),
            fitView: true,
            layerParams: {
              style: feature => {
                const parcelTerrit = feature.get('teryt');
                return new Style({
                  stroke: new Stroke({
                    color: 'rgba(191, 54, 12, 1)',
                    width: 2,
                  }),
                  ...(parcelTerrit
                    ? {
                        text: new Text({
                          text: parcelTerrit,
                          font: 'normal 14px sans-serif',
                          fill: new Fill({ color: 'rgba(191, 54, 12, 1)' }),
                          stroke: new Stroke({
                            color: '#FFFFFF',
                            opacity: 1,
                            width: 3,
                            dash: [],
                          }),
                          overflow: true,
                        }),
                      }
                    : {}),
                });
              },
            },
          });
        }
      }
    },

    getDataSourceAttributeUniqueValues: call('layers/getDataSourceAttributeUniqueValues'),
    uploadDataSource: call('layers/uploadDataSource'),
    openDataSourceDialog() {
      this.dataSourceDialogLoaded = this.dataSourceDialogVisible = true;
    },
    async saveToDataSource() {
      this.dataSourceDialogLoading = true;
      try {
        let geojson;
        if (this.skipDuplicates) {
          const r = await this.getDataSourceAttributeUniqueValues({
            dataSourceName: this.dataSourceName,
            attributeName: this.dataSourceParams['teryt'],
          });
          const uniqueValues = Object.keys(r.values);
          const filteredParcels = this.parcels.filter(parcel => !uniqueValues.includes(parcel['teryt']));
          geojson = this.getGeojson(this.dataSourceParams, filteredParcels);
        } else geojson = this.getGeojson(this.dataSourceParams);

        const blob = new Blob([JSON.stringify(geojson)], { type: 'application/geo+json;charset=utf-8;' });
        const file = new File([blob], 'geojson.geojson');
        const formData = new FormData();
        formData.append('geojson', file);
        formData.append('datasource_name', this.dataSourceName);
        await this.uploadDataSource(formData);
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('sidebar.measurementsModule.successSaveFeatures'),
        });
        this.projectLayers.forEach(layer => {
          if (layer.data_source_name === this.dataSourceName) {
            this.$root.$emit('refreshMvtSource', layer.id);
          }
        });
      } finally {
        this.dataSourceDialogLoading = false;
        this.dataSourceDialogVisible = false;
      }
    },

    exportGeojsonToShapefile: call('layers/exportGeojsonToShapefile'),
    async downloadShapefile() {
      this.loading = true;
      try {
        const geojson = this.getGeojson();
        await this.exportGeojsonToShapefile(geojson);
      } finally {
        this.loading = false;
      }
    },
    getGeojson(attrsMapping, parcels = this.parcels) {
      return {
        crs: {
          properties: {
            name: this.$_config.defaultEpsg,
          },
          type: 'name',
        },
        features: parcels.map((parcel, index) => {
          return {
            geometry: parcel.geometry,
            ...(attrsMapping ? {} : { id: index + 1 }),
            properties: ['commune', 'county', 'numer', 'region', 'teryt', 'voivodeship', 'sheet'].reduce(
              (total, current) => {
                if (attrsMapping) {
                  if (attrsMapping[current]) total[attrsMapping[current]] = parcel[current] || null;
                } else {
                  total[current] = parcel[current] || null;
                }
                return total;
              },
              {}
            ),
            type: 'Feature',
          };
        }),
        type: 'FeatureCollection',
        name: 'parcels',
      };
    },
  },
};
</script>
<style lang="scss" scoped>
.parcels-search-element {
  border-radius: 8px;
  height: 36px;
}
.parcels-search-element-success {
  background-color: rgba(map-get($colors, 'success'), 0.1);
}
.parcels-search-element-error {
  background-color: rgba(map-get($colors, 'error'), 0.1);
}
</style>
